import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { crmErrorMessage } from "../api/crmErrors";
import { BEFORE_SESSION_TRIGGERS } from "../contract";
import useSequence from "../hooks/useSequence";
import useCrmSettings from "../hooks/useCrmSettings";
import { crmHref } from "../paths";
import CrmButton from "../ui/CrmButton";
import CrmInput from "../ui/CrmInput";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import CrmAlertDialog from "../ui/CrmAlertDialog";
import TriggerSelect from "../email/editor/TriggerSelect";
import StepEditor from "../email/editor/StepEditor";
import { CRM } from "../../../constants/testIds/crm";

/**
 * Admin → CRM → Email Automation → Sequence editor (spec Sequence editor, owner-only):
 * name, trigger, steps (one expanded at a time), Save draft / Activate.
 *
 * The header name is a plain input rather than the product's usual click-to-save
 * `InlineEditableField` (used elsewhere in the CRM): this screen's persistence is
 * BATCHED behind "Save draft"/"Activate", so a field that commits on blur would either
 * half-save the sequence (name persisted, steps not) or fight the draft/dirty model this
 * screen needs for its unsaved-changes guard. `dirty` (from `useSequence`) drives both.
 */
export default function CrmSequenceEditor() {
  const t = useT("crm");
  const tc = useT("common");
  const { sequenceId } = useParams();
  const navigate = useNavigate();
  const {
    sequence, draft, loading, loadError, reload, dirty,
    setName, setTrigger, addStep, updateStep, removeStep, saveDraft, activate, stepEmailDraft,
  } = useSequence(sequenceId);
  const { settings: crmSettings } = useCrmSettings();
  const [openStep, setOpenStep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // beforeunload only (design §F3, no `useBlocker`: BrowserRouter, not a data router).
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => { if (draft?.steps?.length) setOpenStep((i) => (i == null ? 0 : i)); }, [draft?.steps?.length]);

  const back = () => { if (dirty) setLeaving(true); else navigate(crmHref("/email")); };
  const leaveAnyway = () => { setLeaving(false); navigate(crmHref("/email")); };

  const run = async (action, okKey) => {
    setBusy(true);
    try {
      await action();
      if (okKey) toast.success(t(okKey));
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return <CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.seqError} />;
  if (loading || !draft) return <div data-testid={CRM.seq} className="h-64 animate-pulse rounded-2xl bg-eh-cream" aria-busy="true" />;

  const locked = !!sequence.system_key;
  const isReminders = sequence.system_key === "session_reminders";
  const anchors = BEFORE_SESSION_TRIGGERS.includes(draft.trigger?.type) ? ["after_trigger", "before_session"] : ["after_trigger"];

  return (
    <div data-testid={CRM.seq} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CrmButton variant="link" onClick={back}>{t("common.back")}</CrmButton>
          <CrmInput value={draft.name} placeholder={t("seq.namePlaceholder")} onChange={(e) => setName(e.target.value)}
            data-testid={CRM.seqName} className="font-display text-xl w-72" />
        </div>
        <div className="flex items-center gap-3">
          <CrmButton variant="link" disabled={busy} onClick={() => run(saveDraft, "seq.savedDraft")} data-testid={CRM.seqSave}>
            {t("seq.saveDraft")}
          </CrmButton>
          <CrmButton disabled={busy} onClick={() => run(activate, "seq.activated")} data-testid={CRM.seqActivate}>
            {t("seq.activate")}
          </CrmButton>
        </div>
      </div>

      <TriggerSelect trigger={draft.trigger} onChange={setTrigger} locked={locked}
        reviewThreshold={crmSettings?.review_threshold} />

      <div className="space-y-3">
        {draft.steps.map((step, i) => (
          <StepEditor
            key={step.id}
            step={step}
            index={i}
            anchors={anchors}
            isReminders={isReminders}
            isOpen={openStep === i}
            onToggleOpen={() => setOpenStep((cur) => (cur === i ? null : i))}
            onChange={(patch) => updateStep(step.id, patch)}
            onDelete={() => { removeStep(step.id); setOpenStep((cur) => (cur === i ? null : cur)); }}
            emailDraft={stepEmailDraft(step)}
          />
        ))}
      </div>

      <CrmButton variant="outline" onClick={() => { addStep(sequence.system_key === "follow_up" || sequence.system_key === "re_engagement"); setOpenStep(draft.steps.length); }} data-testid={CRM.seqAddStep}>
        {t("seq.addStep")}
      </CrmButton>

      <CrmAlertDialog
        open={leaving}
        onOpenChange={setLeaving}
        tone="danger"
        testId={CRM.seqUnsavedDialog}
        title={t("seq.unsavedTitle")}
        description={t("seq.unsavedBody")}
        confirmLabel={t("seq.unsavedLeave")}
        cancelLabel={t("seq.unsavedStay")}
        onConfirm={leaveAnyway}
        confirmTestId={CRM.seqUnsavedLeave}
      />
    </div>
  );
}
